/**
 * Il testo della copertina, come dati.
 *
 * Sta fuori dai componenti per lo stesso motivo per cui nel client la logica sta
 * fuori dalle viste: così è verificabile senza un browser. Il test accanto
 * controlla ciò che a occhio non si vede — passi numerati senza buchi, chiavi
 * uniche, nessun campo vuoto, nessuna icona inesistente — e la copertina è
 * esattamente il posto dove una svista del genere resta online per settimane.
 *
 * I numeri citati qui sono i default di `DEFAULT_AUCTION_SETTINGS`
 * (`@falasta/contracts`). Non sono importati: la copertina è un sito statico e
 * non deve trascinarsi dietro i contratti per scrivere «3 secondi». Se i
 * default cambiano, questo file va aggiornato a mano — per questo i valori
 * stanno tutti in `AUCTION_DEFAULTS` e non sparsi nelle frasi.
 */

/** Le icone disegnate da `FeatureIcon.vue`; il tipo impedisce di citarne una che non esiste. */
export type IconName = 'gavel' | 'bolt' | 'sheet' | 'phone' | 'shield' | 'rewind';

export interface Capability {
  readonly id: string;
  readonly icon: IconName;
  readonly title: string;
  readonly body: string;
}

/** Chi fa la mossa: la copertina deve dire subito che non è tutto in capo all'amministratore. */
export type Actor = 'Amministratore' | 'Fantallenatore' | 'Falasta';

export interface Step {
  readonly id: string;
  /** Progressivo mostrato accanto al passo: contiguo da 1, garantito dal test. */
  readonly number: number;
  readonly actor: Actor;
  readonly title: string;
  readonly body: string;
}

export interface Guarantee {
  readonly id: string;
  readonly title: string;
  readonly body: string;
}

export interface FaqEntry {
  readonly id: string;
  readonly question: string;
  readonly answer: string;
}

export interface Fact {
  readonly id: string;
  readonly value: string;
  readonly label: string;
}

/** I default di configurazione, in un posto solo: le frasi li interpolano. */
export const AUCTION_DEFAULTS = {
  bidTimeoutSeconds: 3,
  callIntervalSeconds: 5,
  initialBudget: 500,
  rosterLimits: { P: 3, D: 8, C: 8, A: 6 },
} as const;

export const TAGLINE = 'L’asta del fantacalcio, condotta dal server.';

export const INTRO =
  'Tu chiami la lega attorno a un tavolo o a una videochiamata: Falasta mette in mezzo il ' +
  'banditore. Chiama i calciatori uno alla volta, cronometra i rilanci e aggiudica allo scadere. ' +
  'Nessuno deve più tenere il conto dei crediti su un foglio, e non si discute su chi ha detto ' +
  '«35» per primo.';

export const FACTS: readonly Fact[] = [
  {
    id: 'timeout',
    value: `${AUCTION_DEFAULTS.bidTimeoutSeconds}s`,
    label: 'di countdown dopo ogni rilancio, configurabili',
  },
  {
    id: 'budget',
    value: `${AUCTION_DEFAULTS.initialBudget}`,
    label: 'crediti a testa di default, budget per asta',
  },
  {
    id: 'roster',
    value: `${AUCTION_DEFAULTS.rosterLimits.P}-${AUCTION_DEFAULTS.rosterLimits.D}-${AUCTION_DEFAULTS.rosterLimits.C}-${AUCTION_DEFAULTS.rosterLimits.A}`,
    label: 'slot per reparto, modificabili prima dell’avvio',
  },
  {
    id: 'parallel',
    value: '∞',
    label: 'aste in parallelo: ognuna ha il suo stato e i suoi tempi',
  },
];

export const CAPABILITIES: readonly Capability[] = [
  {
    id: 'engine',
    icon: 'gavel',
    title: 'Chiama e aggiudica da solo',
    body:
      'Il motore scorre la sequenza reparto per reparto: apre la chiamata, riparte il countdown a ' +
      'ogni rilancio, aggiudica a chi ha offerto per ultimo e passa al calciatore dopo ' +
      `${AUCTION_DEFAULTS.callIntervalSeconds} secondi. L’amministratore conduce, non fa da cronometro.`,
  },
  {
    id: 'realtime',
    icon: 'bolt',
    title: 'Tutti vedono la stessa cosa',
    body:
      'Chiamata, prezzo, tempo residuo e budget arrivano a ogni schermo nello stesso istante. Il ' +
      'countdown disegnato dal browser è corretto sullo scarto d’orologio col server, così ' +
      'nessuno perde un rilancio perché ha il telefono avanti di due secondi.',
  },
  {
    id: 'import',
    icon: 'sheet',
    title: 'La rosa arriva da un Excel',
    body:
      'Un .xlsx con le colonne id, nome, squadra, ruolo, valore. Se il file ha un errore ' +
      'ricevi un rapporto riga per riga con il numero di riga che leggi in Excel, e non viene ' +
      'importato nulla: mai mezza rosa dentro.',
  },
  {
    id: 'invite',
    icon: 'phone',
    title: 'I fantallenatori entrano con un link',
    body:
      'Nessuna password da creare, nessun account da gestire: ognuno riceve per email il proprio ' +
      'magic link ed entra dal telefono nella sua asta, con il suo budget e la sua rosa. ' +
      'Revocabile quando vuoi.',
  },
  {
    id: 'rules',
    icon: 'shield',
    title: 'Le regole le tiene il server',
    body:
      'Budget insufficiente, reparto già pieno, offerta arrivata a chiamata chiusa: rifiutate ' +
      'dal motore, non dal bottone. Chi prova a forzare la mano dal browser ottiene lo stesso ' +
      'risultato di chi non ci prova.',
  },
  {
    id: 'undo',
    icon: 'rewind',
    title: 'Si può tornare indietro',
    body:
      'Un’assegnazione sbagliata si annulla e i crediti tornano al loro posto; se serve, ' +
      'l’asta si riporta a un punto preciso della sequenza. Niente viene cancellato: resta ' +
      'scritto chi ha annullato, quando e perché.',
  },
];

export const STEPS: readonly Step[] = [
  {
    id: 'create',
    number: 1,
    actor: 'Amministratore',
    title: 'Crei l’asta e carichi i calciatori',
    body:
      'Ti registri, dai un nome all’asta e carichi il listone in .xlsx. Puoi tenerne aperte ' +
      'più di una: la lega degli amici e quella dell’ufficio non si toccano.',
  },
  {
    id: 'configure',
    number: 2,
    actor: 'Amministratore',
    title: 'Decidi tempi, crediti e rose',
    body:
      `Durata del countdown (${AUCTION_DEFAULTS.bidTimeoutSeconds} secondi di default), pausa fra ` +
      'una chiamata e l’altra, budget iniziale e slot per reparto. Si cambia finché l’asta ' +
      'non parte: dopo, i crediti sono in gioco e la configurazione si blocca.',
  },
  {
    id: 'invite',
    number: 3,
    actor: 'Amministratore',
    title: 'Inviti i fantallenatori',
    body:
      'Nome ed email: parte l’invito. Aggiungerne uno in più a due giorni dall’asta è una ' +
      'riga, non una riconfigurazione.',
  },
  {
    id: 'sequence',
    number: 4,
    actor: 'Amministratore',
    title: 'Generi l’ordine di chiamata',
    body:
      'Si procede per reparto — portieri, difensori, centrocampisti, attaccanti — e dentro ogni ' +
      'reparto in ordine alfabetico a partire dalla lettera che scegli, oppure a caso. Il caso è ' +
      'riproducibile da un seme: l’ordine è deciso prima e non lo vede nessuno.',
  },
  {
    id: 'join',
    number: 5,
    actor: 'Fantallenatore',
    title: 'Apri il link e sei dentro',
    body:
      'Dal telefono vedi chi è in chiamata, il prezzo, il tempo che resta, i tuoi crediti e la tua ' +
      'rosa che si riempie. Rilanci con un pollice; il calciatore in chiamata viene anche ' +
      'annunciato a voce, se lasci l’audio acceso.',
  },
  {
    id: 'run',
    number: 6,
    actor: 'Falasta',
    title: 'L’asta va avanti da sola',
    body:
      'Ogni rilancio fa ripartire il countdown; allo scadere il calciatore è di chi ha offerto per ' +
      'ultimo, i crediti si scalano e parte la chiamata successiva. Chi resta invenduto torna in ' +
      'gara più avanti, quando i budget sono cambiati.',
  },
  {
    id: 'control',
    number: 7,
    actor: 'Amministratore',
    title: 'Resti tu il padrone di casa',
    body:
      'Pausa e ripresa, ricomincia questa chiamata, salta a un reparto, annulla un’assegnazione, ' +
      'riporta indietro l’asta. Se cade la connessione — la tua o quella del server — al rientro ' +
      'l’asta è al punto in cui l’avevi lasciata.',
  },
  {
    id: 'close',
    number: 8,
    actor: 'Falasta',
    title: 'Alla fine porti via le rose',
    body:
      'L’asta si chiude quando non c’è più niente da chiamare, di norma perché le rose sono ' +
      'complete. Amministratore e fantallenatori scaricano il risultato in .xlsx.',
  },
];

export const GUARANTEES: readonly Guarantee[] = [
  {
    id: 'authority',
    title: 'Decide il server, non la connessione più veloce',
    body:
      'Il browser disegna il countdown, ma la scadenza la stabilisce il motore. Due offerte nello ' +
      'stesso istante vengono messe in fila da lui, una per volta, per ogni asta.',
  },
  {
    id: 'idempotent',
    title: 'Un doppio tocco vale un’offerta sola',
    body:
      'Ogni comando porta un identificativo e viene eseguito una volta sola: un dito nervoso, una ' +
      'rete ballerina o un rinvio del broker non producono due rilanci.',
  },
  {
    id: 'durable',
    title: 'Se qualcosa si spegne, l’asta riprende',
    body:
      'Lo stato sta nel database, non nella memoria di un processo. Al riavvio i timer si ' +
      'riarmano da dove erano, e una scadenza già passata viene chiusa con l’esito che avrebbe ' +
      'avuto comunque.',
  },
  {
    id: 'history',
    title: 'Ogni mossa lascia una traccia',
    body:
      'Offerte, aggiudicazioni, annullamenti: tutto è registrato con autore e istante, nella ' +
      'stessa transazione di ciò che descrive. A fine asta si può ricostruire qualsiasi ' +
      'contestazione.',
  },
];

export const FAQ: readonly FaqEntry[] = [
  {
    id: 'install',
    question: 'I fantallenatori devono installare qualcosa?',
    answer:
      'No. Serve un browser: si apre il link ricevuto per email e si è dentro. Niente app da ' +
      'scaricare, niente password da inventare.',
  },
  {
    id: 'together',
    question: 'Bisogna essere tutti nella stessa stanza?',
    answer:
      'No, ma funziona bene anche così. Chi è a casa e chi è al tavolo vedono la stessa chiamata ' +
      'e lo stesso countdown: l’asta a distanza smette di essere un problema di fiducia.',
  },
  {
    id: 'disconnect',
    question: 'E se a qualcuno cade la linea durante l’asta?',
    answer:
      'L’asta non si ferma per un partecipante disconnesso: al rientro ritrova la chiamata in ' +
      'corso, i suoi crediti e la sua rosa aggiornati. Se serve tempo, l’amministratore mette ' +
      'in pausa.',
  },
  {
    id: 'mistake',
    question: 'Ho aggiudicato un calciatore per sbaglio. Si rimedia?',
    answer:
      'Sì: l’assegnazione si annulla, i crediti tornano al fantallenatore e il calciatore torna ' +
      'chiamabile. Se il pasticcio è più grande, l’asta si riporta a una posizione precedente ' +
      'della sequenza e riparte da lì, in pausa, quando dici tu.',
  },
  {
    id: 'unsold',
    question: 'Che fine fanno i calciatori che non compra nessuno?',
    answer:
      'Restano in lista. Quando il reparto finisce si ricomincia dai suoi invenduti: nel frattempo ' +
      'i budget si sono svuotati e le scelte cambiano. Si passa al reparto dopo quando non c’è ' +
      'più niente da vendere o nessuno ha più slot liberi lì.',
  },
];
